import React, { useState, useRef, useContext, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import ClientsTable from "../components/client-table/ClientsTable";
import DocumentsPageHeader from "../components/common/DocumentsPageHeader";
import ClientDocuments from "../components/client-table/ClientDocuments";
import { AuthContext } from "../context/Auth/AuthContext";
import "../styles/Clients.css";
import "../styles/Documents.css";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";

// mode="chasing" (Clients > Document Chasing): the same Clients list; picking
// a client opens only that client's Document Chasing view (existing
// DocumentChasingPage), and the URL keeps the client (/document-chasing/:cid).
const Clients = ({ mode }) => {
  const chasing = mode === "chasing";
  const basePath = chasing ? "/document-chasing" : "/clients";
  const location = useLocation();
  const pathname = location.pathname;
  const showUploads = pathname.endsWith("/uploads") ? "/uploads" : null;

  const { cid: clientId } = useParams();
  const { token, id, is_admin } = useContext(AuthContext);

  // Replaces the old array-of-tabs model: null shows the main Clients list,
  // a client object shows that client's documents directly on the page (no
  // internal client-tab strip - the previously separate "Primary" /
  // beneficiary switcher inside ClientDocuments/RequestedDocumentsList is
  // unrelated sub-navigation within a single client and stays as-is).
  const [selectedClient, setSelectedClient] = useState(null);
  const [showAddClientForm, setShowAddClientForm] = useState(false);
  const [showClientIdSearch, setShowClientIdSearch] = useState(false);
  const onAddClientButtonClick = () => setShowAddClientForm((v) => !v);
  const addClientActionRef = useRef(onAddClientButtonClick);

  // Selecting a client re-renders this same page without remounting it
  // (routes like /clients/:cid share one Route match), so an open "Add New
  // Client" popup must be closed explicitly here or it stays open in the
  // background and reappears when the user navigates back.
  useEffect(() => {
    setShowAddClientForm(false);
  }, [selectedClient]);

  const addClientTab = (client) => {
    setSelectedClient(client);
    if (chasing && client?.contact_id) {
      window.history.replaceState(window.history.state, "", `${basePath}/${client.contact_id}`);
    }

    if (clientId !== undefined && clientId === client.contact_id) {
      setShowClientIdSearch(true);
    }
  };

  const backToClientsList = () => {
    setSelectedClient(null);
    if (clientId !== undefined) {
      window.location.assign(basePath);
    } else if (chasing) {
      window.history.replaceState(window.history.state, "", basePath);
    }
  };

  return (
    <div className="document-container">
      <div className="navbar">
        <Navbar />
      </div>
      <div className="document-content">
        {!showAddClientForm && !selectedClient && (
          <DocumentsPageHeader
            showBreadcrumb={false}
            headerSpacingClass="py-3"
            breadcrumbs={[{ label: chasing ? "Document Chasing" : "Clients" }]}
            icon={chasing ? MarkEmailUnreadOutlinedIcon : GroupOutlinedIcon}
            title={chasing ? "Document Chasing" : "All Clients"}
            description={
              chasing
                ? "Select a client to view and send document chasing reminders"
                : "Manage clients and their associated documents"
            }
            action={
              chasing ? null : (
              <button
                className="add-document shrink-0"
                onClick={() => addClientActionRef.current()}
              >
                <PersonAddIcon sx={{ fontSize: 18 }} />
                Add Client
              </button>
              )
            }
          />
        )}
        {!showAddClientForm && selectedClient && (
          <DocumentsPageHeader
            showHeader={false}
            breadcrumbs={[
              { label: chasing ? "Document Chasing" : "Clients", onClick: backToClientsList },
              { label: selectedClient.name },
            ]}
          />
        )}

        <div className="tab-content">
          {!selectedClient ? (
            <ClientsTable
              addClientTab={addClientTab}
              showAddClientForm={showAddClientForm}
              onAddClientButtonClick={onAddClientButtonClick}
              clientId={clientId}
              showClientIdSearch={showClientIdSearch}
              addClientActionRef={addClientActionRef}
              chasingOnly={chasing}
            />
          ) : (
            <ClientDocuments
              clientID={selectedClient.contact_id}
              clientEmail={selectedClient.email_id}
              clientType={selectedClient.type}
              setShowClientIdSearch={setShowClientIdSearch}
              showUploads={showUploads}
              beneficiary={selectedClient.beneficiary}
              typeOptions={selectedClient.typeOptions}
              clientSubType={selectedClient.sub_type}
              clientPhone={
                selectedClient.country_code + selectedClient.phone_number
              }
              chasingOnly={chasing}
              clientRecord={selectedClient}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Clients;
